import React from 'react'
import { Conditions, MenoscaleConditions, Quiz, QuizBase, QuizConditions } from 'redux/quiz/types'
import * as funnel from '../../funnel'
import {
  ContentScreenProps,
  EmailCaptureScreenProps,
  ExitScreenProps,
  InputHeightScreenProps,
  InputTargetWeightScreenProps,
  InputWeightScreenProps,
  MandatoryConsentScreenProps,
  MultiSelectScreenProps,
  QuizAnswerScreenProps,
  SelectScreenProps,
  SocialProofScreenProps,
  HealthSummaryScreenProps,
  WeightCalculationScreenProps,
  FutureYouScreenProps,
  ZiggieTalkScreenProps,
} from '../../funnel'
import { ResultsLoadingScreenProps } from '../../funnel/ResultsLoadingScreen'
import { SymptomScoreCalculatingScreenProps } from '../../funnel/SymptomScoreCalculatingScreen'
import { SymptomScoreResultsScreenProps } from '../../funnel/SymptomScoreResultsScreen'
import { FutureScoreScreenProps } from '../../funnel/FutureScoreScreen'
import { SymptomScoreCalculatingScreenE7Props } from '../../funnel/SymptomScoreCalculatingScreenE7'
import { SymptomScoreResultsScreenE7Props } from '../../funnel/SymptomScoreResultsScreenE7'
import { FutureScoreScreenE7Props } from '../../funnel/FutureScoreScreenE7'
import { PlanProgressScreenProps } from '../../funnel/PlanProgressScreen'
import { ZoePromiseScreenProps } from '../../funnel/ZoePromiseScreen'
import { OnAdviceSubmitCallback, onBackClickCallback, OnFormSubmitCallback, onNextClickCallback } from '../../types'
import { weightPlanTypeFromQuiz } from '../../../libs/weight/weightCalculations'
import { evaluateCondition } from '../../utils'
import { AltPropsSupport, LegacyQuizScreen, QuizScreen, QuizVersion, ScreenType } from '../../../config/types'
import DEFAULT_QUIZ_CONFIG from '../../../config/quiz.config'
import { goalAnswersToBeSubmitted } from '../../../data/goalAnswersToBeSubmitted'
import { updateScreens } from '../../../experiments'
import { ALL_EXPERIMENTS, ExperimentNames } from '../../../config/experiments.config'
import { MultipleSlidersScreen, MultipleSlidersScreenProps } from '../../core/screens/MultipleSlidersScreen'
import { MenoScaleAboutMeScreen, MenoScaleAboutMeScreenProps } from '../../menoscale/MenoScaleAboutMeScreen'
import { MenoScaleResultsScreen, MenoScaleResultsScreenProps } from '../../menoscale/MenoScaleResultsScreen'
import { MenoscaleConsentScreen, MenoscaleConsentScreenProps } from '../../core/screens/MenoscaleConsentScreen'

const supportsAltProps = (screen: any): screen is AltPropsSupport<unknown> => {
  return 'useAltProps' in screen && 'altProps' in screen
}

const getScreenProps = (screen: QuizScreen<unknown>, conditionValues: Required<Conditions>) => {
  if (supportsAltProps(screen)) {
    return evaluateCondition(screen.useAltProps, conditionValues) ? screen.altProps : screen.props
  }
  return screen.props
}

const createComponent = (
  answers: QuizBase,
  conditionValues: Required<Conditions>,
  screen: ScreenType,
  onFormSubmit: OnFormSubmitCallback,
  onAdviceSubmit: OnAdviceSubmitCallback,
  onEmailSubmit: OnFormSubmitCallback,
  onNextClick: onNextClickCallback,
  onBackClick: onBackClickCallback,
): JSX.Element => {
  const props = getScreenProps(screen, conditionValues)
  switch (screen.type) {
    case 'SelectScreen':
      const selectProps = props as SelectScreenProps
      return (
        <funnel.SelectScreen
          onNextClick={onFormSubmit}
          onBackClick={onBackClick}
          conditionValues={conditionValues}
          quiz={answers}
          goalAnswersToBeSubmitted={
            selectProps.includeWeightGoalAnswers ? goalAnswersToBeSubmitted(answers, 0) : undefined
          }
          {...selectProps}
        />
      )
    case 'MultiSelectScreen':
      const multiSelectProps = props as MultiSelectScreenProps
      return (
        <funnel.MultiSelectScreen
          onNextClick={onFormSubmit}
          onBackClick={onBackClick}
          conditionValues={conditionValues}
          quiz={answers}
          goalAnswersToBeSubmitted={
            multiSelectProps.includeWeightGoalAnswers ? goalAnswersToBeSubmitted(answers, 0) : undefined
          }
          {...multiSelectProps}
        />
      )
    case 'MultipleSlidersScreen':
      return (
        <MultipleSlidersScreen
          onNextClick={onFormSubmit}
          onBackClick={onBackClick}
          quiz={answers}
          {...(props as MultipleSlidersScreenProps)}
        />
      )
    case 'ContentScreen':
      return (
        <funnel.ContentScreen
          onNextClick={onNextClick}
          onBackClick={onBackClick}
          quiz={answers}
          {...(props as ContentScreenProps)}
        />
      )
    case 'SocialProofScreen':
      return (
        <funnel.SocialProofScreen onNextClick={onNextClick} quiz={answers} {...(props as SocialProofScreenProps)} />
      )
    case 'MandatoryConsentScreen':
      return (
        <funnel.MandatoryConsentScreen
          onNextClick={onFormSubmit}
          onBackClick={onBackClick}
          quiz={answers}
          {...(props as MandatoryConsentScreenProps)}
        />
      )
    case 'InputHeightScreen':
      return (
        <funnel.InputHeightScreen
          onNextClick={onFormSubmit}
          onBackClick={onBackClick}
          quiz={answers}
          {...(props as InputHeightScreenProps)}
        />
      )
    case 'InputWeightScreen':
      return (
        <funnel.InputWeightScreen
          onNextClick={onFormSubmit}
          onBackClick={onBackClick}
          quiz={answers}
          {...(props as InputWeightScreenProps)}
        />
      )
    case 'InputTargetWeightScreen':
      return (
        <funnel.InputTargetWeightScreen
          onNextClick={onFormSubmit}
          onBackClick={onBackClick}
          quiz={answers}
          {...(props as InputTargetWeightScreenProps)}
        />
      )
    case 'EmailCaptureScreen':
      const emailProps = props as EmailCaptureScreenProps
      return (
        <funnel.EmailCaptureScreen
          onNextClick={(values) => onEmailSubmit(values, answers)}
          onBackClick={onBackClick}
          {...emailProps}
        />
      )
    case 'MenoscaleConsentScreen':
      return (
        <MenoscaleConsentScreen
          onNextClick={onFormSubmit}
          onBackClick={onBackClick}
          {...(props as MenoscaleConsentScreenProps)}
        />
      )
    case 'ResultsLoadingScreen':
      return <funnel.ResultsLoadingScreen {...(props as ResultsLoadingScreenProps)} />
    case 'ExitScreen':
      return <funnel.ExitScreen {...(props as ExitScreenProps)} />
    case 'QuizAnswerScreen':
      return <funnel.QuizAnswerScreen onNextClick={onNextClick} quiz={answers} {...(props as QuizAnswerScreenProps)} />
    case 'HealthSummaryScreen':
      return (
        <funnel.HealthSummaryScreen
          onNextClick={onNextClick}
          quiz={answers}
          conditionValues={conditionValues as Record<string, string | string[] | undefined>}
          {...(props as HealthSummaryScreenProps)}
        />
      )
    case 'MenoScaleAboutMeScreen':
      return (
        <MenoScaleAboutMeScreen
          onNextClick={onFormSubmit}
          onBackClick={onBackClick}
          quiz={answers}
          {...(props as MenoScaleAboutMeScreenProps)}
        />
      )
    case 'MenoScaleResultsScreen':
      return (
        <MenoScaleResultsScreen
          onNextClick={onNextClick}
          onBackClick={onBackClick}
          quiz={answers}
          {...(props as MenoScaleResultsScreenProps)}
        />
      )
    case 'SymptomScoreCalculatingScreen':
      return (
        <funnel.SymptomScoreCalculatingScreen
          onNextClick={onNextClick}
          {...(props as SymptomScoreCalculatingScreenProps)}
        />
      )
    case 'SymptomScoreResultsScreen':
      return (
        <funnel.SymptomScoreResultsScreen
          onNextClick={onNextClick}
          quiz={answers}
          {...(props as SymptomScoreResultsScreenProps)}
        />
      )
    case 'FutureScoreScreen':
      return (
        <funnel.FutureScoreScreen onNextClick={onNextClick} quiz={answers} {...(props as FutureScoreScreenProps)} />
      )
    case 'SymptomScoreCalculatingScreenE7':
      return (
        <funnel.SymptomScoreCalculatingScreenE7
          onNextClick={onNextClick}
          {...(props as SymptomScoreCalculatingScreenE7Props)}
        />
      )
    case 'SymptomScoreResultsScreenE7':
      return (
        <funnel.SymptomScoreResultsScreenE7
          onNextClick={onNextClick}
          quiz={answers}
          {...(props as SymptomScoreResultsScreenE7Props)}
        />
      )
    case 'FutureScoreScreenE7':
      return (
        <funnel.FutureScoreScreenE7 onNextClick={onNextClick} quiz={answers} {...(props as FutureScoreScreenE7Props)} />
      )
    case 'PlanProgressScreen':
      return (
        <funnel.PlanProgressScreen onNextClick={onNextClick} quiz={answers} {...(props as PlanProgressScreenProps)} />
      )
    case 'ZoePromiseScreen':
      return <funnel.ZoePromiseScreen onNextClick={onNextClick} {...(props as ZoePromiseScreenProps)} />
    case 'WeightCalculationScreen':
      return (
        <funnel.WeightCalculationScreen
          onNextClick={onNextClick}
          quiz={answers}
          {...(props as WeightCalculationScreenProps)}
        />
      )
    case 'FutureYouScreen':
      return <funnel.FutureYouScreen onNextClick={onNextClick} quiz={answers} {...(props as FutureYouScreenProps)} />
    case 'ZiggieTalkScreen':
      return <funnel.ZiggieTalkScreen onNextClick={onNextClick} quiz={answers} {...(props as ZiggieTalkScreenProps)} />
  }
}

const applyExperimentOverrides = (screens: ScreenType[], experiments: ExperimentNames[]): ScreenType[] => {
  const overrides = experiments
    .map((experimentName) => ALL_EXPERIMENTS[experimentName])
    .filter((overrides) => !!overrides)
    .flat()
  return updateScreens(screens, overrides)
}

export const createQuizScreens = (
  quiz: Quiz,
  quizVersion: QuizVersion,
  experiments: ExperimentNames[],
  quizCountry: string | undefined,
  onFormSubmit: OnFormSubmitCallback,
  onAdviceSubmit: OnAdviceSubmitCallback,
  onEmailSubmit: OnFormSubmitCallback,
  onNextClick: onNextClickCallback,
  onBackClick: onBackClickCallback,
): LegacyQuizScreen[] => {
  const answers = quiz[quizVersion] || ({} as QuizBase)
  const conditionValues = calculateConditionValues(answers, quizCountry, quizVersion)
  const defaultScreens = DEFAULT_QUIZ_CONFIG[quizVersion].screens
  const updatedScreens = applyExperimentOverrides(defaultScreens, experiments)
  return updatedScreens.map((screen: ScreenType) => {
    return {
      path: screen.path,
      hideProgressBar: screen.hideProgressBar,
      overlayHeader: screen.overlayHeader,
      // we ignore conditional screens (showWhen) when calculating the progress bar, to give a better sense of progress
      // we also use true as default when isIncludedInProgress is not defined
      isIncludedInProgress: !screen.showWhen && screen.isIncludedInProgress !== false,
      showIf: (quiz: Quiz) => {
        const recentAnswers = quiz[quizVersion] || ({} as QuizBase)
        const recentConditionValues = calculateConditionValues(recentAnswers, quizCountry, quizVersion)
        return evaluateCondition(screen.showWhen, recentConditionValues)
      },
      backgroundColor: screen.backgroundColor,
      topImage: (getScreenProps(screen, conditionValues) as any)?.topImage,
      screenComponent: createComponent(
        answers,
        conditionValues,
        screen,
        onFormSubmit,
        onAdviceSubmit,
        onEmailSubmit,
        onNextClick,
        onBackClick,
      ),
    }
  })
}

const calculateConditionValues = (
  quiz: QuizBase,
  quizCountry: string | undefined,
  version: QuizVersion,
): Required<QuizConditions> | Required<MenoscaleConditions> => {
  // convert all values from the quiz object to strings
  // this means that even questions that are not defined in the type QuizBase (but are included in the quiz config)
  // can be used in the condition evaluation
  const baseValues = Object.entries(quiz ?? {}).map(([key, value]) => [
    key,
    value ? (Array.isArray(value) ? value : String(value)) : '',
  ])
  if (version === 'menoscale') {
    return {
      ...Object.fromEntries(baseValues),
      quiz_country: quizCountry ?? 'false',
    }
  } else {
    const weightPlan = weightPlanTypeFromQuiz(quiz)
    const goalsValue = (quiz as any).goals
    const goalsArray = Array.isArray(goalsValue) ? goalsValue : goalsValue ? String(goalsValue).split(',') : []
    const isCountryDetected = !!quizCountry
    return {
      // all conditions that are part of the quiz object
      ...Object.fromEntries(baseValues),
      // custom conditions below
      weight_plan: weightPlan,
      has_target_date_weight_loss: String(
        quiz.target_event_date_weight_loss && quiz.target_event_date_weight_loss !== 'none',
      ),
      is_frequently_hungry: String(quiz.hunger_level === 'often' || quiz.hunger_level === 'all_the_time'),
      has_weight_loss_goal: String(goalsArray.includes('lose_weight')),
      no_weight_loss_goal: String(!goalsArray.includes('lose_weight')),
      quiz_country: quizCountry ?? 'false',
      is_country_supported: String(isCountryDetected),
      available_in_location: String(quiz.location === 'true' && isCountryDetected),
      has_blood_sugar_or_blood_fast_risk: String(
        quiz.diabetes_or_prediabetes === 'true' ||
          quiz.cardiovascular_disease_in_family === 'true' ||
          quiz.cardiovascular_disease_diagnosed_or_high_cholesterol === 'true' ||
          quiz.blood_pressure_diagnosed === 'true' ||
          quiz.blood_pressure_medication === 'true',
      ),
    }
  }
}
